import type { ProethicaTestKind } from "@constants/demo/proethica-tests";
import type { IconType } from "@memorang/ui";
import type { DiagnosticCardProps, EXAM_STATUS } from "types/tag";

// Picks: Hero = first ACTIVE, else first not-COMPLETE or last. Cards: [frontier, resume] if different (ACTIVE implies non-COMPLETE, so resume is never before frontier), else [frontier, next] clamped. Assumes non-empty.
export const selectProethicaRecommendationIndices = (
	statuses: EXAM_STATUS[],
): { heroIndex: number; cardIndices: [number, number] } => {
	const lastIndex = Math.max(0, statuses.length - 1);
	const resumeIndex = statuses.findIndex((status) => status === "ACTIVE");
	const firstIncomplete = statuses.findIndex((status) => status !== "COMPLETE");
	const frontierIndex = firstIncomplete === -1 ? lastIndex : firstIncomplete;

	if (resumeIndex !== -1 && resumeIndex !== frontierIndex) {
		return {
			heroIndex: resumeIndex,
			cardIndices: [frontierIndex, resumeIndex],
		};
	}
	const start = Math.max(0, Math.min(frontierIndex, statuses.length - 2));
	return {
		heroIndex: frontierIndex,
		cardIndices: [start, Math.min(start + 1, lastIndex)],
	};
};

export type RecommendationSourceProps = {
	id: string;
	title: string;
	status: EXAM_STATUS;
	cta: string;
	hasAccess?: boolean;
	disabled?: boolean;
	thumbnailSource?: string | number | { uri: string };
	thumbnailUrl?: string;
	icon?: string;
	numItems: number;
	typeLabel: string;
	durationLabel?: string;
	testKind?: ProethicaTestKind;
};

export type RecommendationCard = DiagnosticCardProps & {
	id: string;
	hasAccess?: boolean;
	disabled?: boolean;
	testKind?: ProethicaTestKind;
};

export type RecommendationCardHandlers = {
	loading: boolean;
	onSelect: () => void;
};

export const buttonIconForStatus = (status: EXAM_STATUS): IconType =>
	status === "COMPLETE"
		? "chart-bar"
		: status === "ACTIVE"
			? "play-pause"
			: "play";

export const toProethicaRecommendationCard = (
	prop: RecommendationSourceProps,
	{ loading, onSelect }: RecommendationCardHandlers,
): RecommendationCard => {
	const numCompleted = prop.status === "COMPLETE" ? prop.numItems : 0;
	const subtitle = prop.testKind
		? `${prop.typeLabel} • ${prop.numItems} items`
		: `${prop.typeLabel} • ${prop.durationLabel ?? ""}`;
	return {
		id: prop.id,
		title: prop.title,
		subtitle,
		status: prop.status,
		numItems: prop.numItems,
		numCompleted,
		loading,
		hasAccess: prop.hasAccess,
		disabled: prop.disabled,
		testKind: prop.testKind,
		thumbnailSource: prop.thumbnailSource || prop.thumbnailUrl || undefined,
		icon: (prop.icon ?? "play-outline") as IconType,
		handleClickCard: onSelect,
		button: {
			cta: prop.cta,
			icon: buttonIconForStatus(prop.status),
			handleClick: onSelect,
		},
	};
};
